/**
 * PsicoSalud — Skeleton loader.
 *
 * Placeholder visual mientras los datos están cargando. Reduce la
 * percepción de espera y evita saltos de layout cuando aparece el
 * contenido real. Variantes disponibles:
 *
 *   <Skeleton />                  ← línea de texto (default)
 *   <Skeleton variant="heading" />
 *   <Skeleton variant="caption" />
 *   <Skeleton variant="circle" size={48} />
 *   <Skeleton variant="block" height={120} />
 *   <Skeleton variant="button" />
 *   <Skeleton variant="stat" />
 *
 * Compuestos:
 *   <Skeleton.StatCard />
 *   <Skeleton.TableRow columns={4} />
 *   <Skeleton.Table rows={6} columns={5} />
 *   <Skeleton.TimelineItems count={4} />
 *   <Skeleton.Lines count={3} />
 *
 * Decisión de diseño: el efecto shimmer es un overlay con
 * pseudo-elemento, no un gradiente de fondo. Esto permite que el
 * skeleton funcione sobre cualquier superficie sin tener que
 * coordinar el color del gradiente con el fondo de la card.
 */

import type { CSSProperties } from 'react';

type SkeletonVariant = 'text' | 'heading' | 'caption' | 'circle' | 'block' | 'button' | 'stat';

interface SkeletonProps {
  variant?: SkeletonVariant;
  width?: string | number;
  height?: string | number;
  /** Tamaño (ancho y alto) para variant="circle". */
  size?: string | number;
  style?: CSSProperties;
  className?: string;
}

function toPx(value: string | number | undefined): string | undefined {
  if (value === undefined) return undefined;
  return typeof value === 'number' ? `${value}px` : value;
}

function Skeleton({
  variant = 'text',
  width,
  height,
  size,
  style,
  className = '',
}: SkeletonProps) {
  const cssClass = `skeleton is-${variant} ${className}`.trim();
  const computed: CSSProperties = { ...style };

  if (variant === 'circle') {
    const dim = toPx(size);
    if (dim) {
      computed.width = dim;
      computed.height = dim;
    }
  } else {
    if (width !== undefined) computed.width = toPx(width);
    if (height !== undefined) computed.height = toPx(height);
  }

  return <span className={cssClass} style={computed} aria-hidden="true" />;
}

/** Placeholder para una tarjeta de estadística del dashboard. */
function StatCard() {
  return (
    <div className="card stat">
      <Skeleton variant="heading" width="40%" />
      <div style={{ height: 'var(--espacio-3)' }} />
      <Skeleton variant="text" width="70%" />
    </div>
  );
}

/** Una fila de tabla skeleton (columnas configurables). */
function TableRow({ columns = 5 }: { columns?: number }) {
  return (
    <div
      className="skeleton-row"
      style={{ '--skeleton-cols': columns } as CSSProperties}
    >
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton key={i} variant="text" />
      ))}
    </div>
  );
}

/** Bloque de tabla skeleton (múltiples filas dentro de .table-wrapper). */
function Table({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="table-wrapper">
      <div className="skeleton-table" aria-hidden="true">
        {Array.from({ length: rows }).map((_, i) => (
          <TableRow key={i} columns={columns} />
        ))}
      </div>
    </div>
  );
}

/** Lista de encuentros skeleton, respeta la geometría del timeline real. */
function TimelineItems({ count = 4 }: { count?: number }) {
  return (
    <ol className="encounters-timeline" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <li key={i} className="encounter">
          <Skeleton variant="caption" width="30%" />
          <div style={{ height: 'var(--espacio-2)' }} />
          <Skeleton variant="text" width="85%" />
          <div style={{ height: 'var(--espacio-1)' }} />
          <Skeleton variant="text" width="60%" />
        </li>
      ))}
    </ol>
  );
}

/** Stack de líneas (párrafo). La última línea queda al 70% para imitar wrap natural. */
function Lines({ count = 3 }: { count?: number }) {
  return (
    <div className="skeleton-lines" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} variant="text" width={i === count - 1 ? '70%' : '100%'} />
      ))}
    </div>
  );
}

Skeleton.StatCard = StatCard;
Skeleton.TableRow = TableRow;
Skeleton.Table = Table;
Skeleton.TimelineItems = TimelineItems;
Skeleton.Lines = Lines;

export default Skeleton;
